import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format, parse, isValid } from 'date-fns';

/**
 * 表示モード
 */
export type ViewMode = 'day' | 'week' | 'month' | 'slotSummaryWeek' | 'slotSummaryMonth';

/**
 * URLパラメータ
 */
export interface UrlParams {
  /** 表示日付 */
  date: Date;
  /** 表示モード */
  viewMode: ViewMode;
}

/**
 * useUrlState フックの戻り値
 */
export interface UseUrlStateReturn {
  /** 現在のURLパラメータ */
  params: UrlParams;
  /** 日付を設定 */
  setDate: (date: Date) => void;
  /** 表示モードを設定 */
  setViewMode: (mode: ViewMode) => void;
  /** URLパラメータを一括設定 */
  setParams: (params: Partial<UrlParams>) => void;
}

/**
 * 有効な表示モードかどうかを判定
 */
const isValidViewMode = (value: string | null): value is ViewMode => {
  return (
    value === 'day' ||
    value === 'week' ||
    value === 'month' ||
    value === 'slotSummaryWeek' ||
    value === 'slotSummaryMonth'
  );
};

/**
 * 日付文字列をパース
 */
const parseDateString = (dateStr: string | null): Date | null => {
  if (!dateStr) return null;

  // YYYY-MM-DD形式
  const parsed = parse(dateStr, 'yyyy-MM-dd', new Date());
  if (isValid(parsed)) return parsed;

  // YYYY/MM/DD形式
  const parsedSlash = parse(dateStr, 'yyyy/MM/dd', new Date());
  if (isValid(parsedSlash)) return parsedSlash;

  return null;
};

/**
 * URLパラメータによる状態管理フック
 *
 * @description
 * URLのクエリパラメータを使用して、表示日付と表示モードの状態を管理する。
 * ページリロード後も状態が保持される。
 *
 * @returns {UseUrlStateReturn} URLパラメータ管理関数群
 *
 * @example
 * const { params, setDate, setViewMode } = useUrlState();
 * // URLパラメータ: ?date=2025-01-15&view=week
 * // params = { date: Date(2025-01-15), viewMode: 'week' }
 */
export const useUrlState = (): UseUrlStateReturn => {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * 現在のURLパラメータを取得
   */
  const params = useMemo((): UrlParams => {
    const dateStr = searchParams.get('date');
    const yearStr = searchParams.get('year');
    const monthStr = searchParams.get('month');
    const viewModeStr = searchParams.get('view');

    // 日付の決定（優先順位: date > year+month > 今日）
    let date: Date;
    const parsedDate = parseDateString(dateStr);
    if (parsedDate) {
      date = parsedDate;
    } else if (yearStr && monthStr) {
      // year と month パラメータから日付を生成（月の1日）
      const year = parseInt(yearStr, 10);
      const month = parseInt(monthStr, 10);
      if (!isNaN(year) && !isNaN(month) && month >= 1 && month <= 12) {
        date = new Date(year, month - 1, 1);
      } else {
        date = new Date();
      }
    } else {
      date = new Date();
    }

    // 表示モードの決定
    const viewMode = isValidViewMode(viewModeStr) ? viewModeStr : 'day';

    return { date, viewMode };
  }, [searchParams]);

  /**
   * 日付を設定
   */
  const setDate = useCallback(
    (date: Date) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('date', format(date, 'yyyy-MM-dd'));
      // year/month パラメータは削除（dateを使用）
      newParams.delete('year');
      newParams.delete('month');
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  /**
   * 表示モードを設定
   */
  const setViewMode = useCallback(
    (mode: ViewMode) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('view', mode);
      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  /**
   * URLパラメータを一括設定
   */
  const setParams = useCallback(
    (newValues: Partial<UrlParams>) => {
      const newParams = new URLSearchParams(searchParams);

      if (newValues.date) {
        newParams.set('date', format(newValues.date, 'yyyy-MM-dd'));
        newParams.delete('year');
        newParams.delete('month');
      }

      if (newValues.viewMode) {
        newParams.set('view', newValues.viewMode);
      }

      setSearchParams(newParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return {
    params,
    setDate,
    setViewMode,
    setParams,
  };
};
